import React, { useEffect, useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { API_URI } from "../../helpers";

// ────────────────────────────────────────────────────────────────────
//  Tokens — misma paleta del /inbox (slate light + indigo primary)
// ────────────────────────────────────────────────────────────────────
const T = {
  bg:        "#F8FAFC",  // canvas slate-50
  bgDeep:    "#F1F5F9",  // table head slate-100
  surface:   "#FFFFFF",
  surface2:  "#FAFBFC",  // detail bg del inbox
  line:      "#E2E8F0",  // slate-200
  lineSoft:  "#F1F5F9",  // slate-100
  ink:       "#0F172A",  // slate-900
  ink2:      "#334155",  // slate-700
  ink3:      "#64748B",  // slate-500
  ink4:      "#94A3B8",  // slate-400
  hover:     "#F8FAFC",
  indigo:    "#6366F1",
  indigoDeep:"#4F46E5",
  indigoSoft:"#EEF2FF",
  // amber sólo para microacento, igual que el inbox lo usa solo en el sidebar.
  amber:     "#e9b163",
  fontSans:  "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, sans-serif",
};

// ────────────────────────────────────────────────────────────────────
//  Iconos inline (stroke 1.75, ámbar/ink)
// ────────────────────────────────────────────────────────────────────
const Icon = ({ name, size = 16, color = "currentColor", strokeWidth = 1.75 }) => {
  const paths = {
    filter:   "M22 3H2l8 9.46V19l4 2v-8.54L22 3z",
    download: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3",
    refresh:  "M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8 M21 3v5h-5 M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16 M8 16H3v5",
    calendar: "M8 2v4 M16 2v4 M3 10h18 M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z",
    mail:     "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6 12 13 2 6",
    spark:    "M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.582a.5.5 0 0 1 0 .962L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z",
    arrow:    "M5 12h14 M12 5l7 7-7 7",
    alert:    "M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z M12 9v4 M12 17h.01",
    check:    "M20 6 9 17l-5-5",
    x:        "M18 6 6 18 M6 6l12 12",
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d={paths[name]} />
    </svg>
  );
};

// Pills de estado — mismos tonos que STATUS_PILL del /inbox.
const STATE_PILL = {
  register: { label: "Registrado", bg: "#FEF3C7", fg: "#92400E" },
  active:   { label: "Activo",     bg: "#D1FAE5", fg: "#065F46" },
  expire:   { label: "Vencido",    bg: "#E5E7EB", fg: "#374151" },
  cancel:   { label: "Cancelado",  bg: "#FEE2E2", fg: "#991B1B" },
  new:      { label: "Nuevo",      bg: "#DBEAFE", fg: "#1E40AF" },
  na:       { label: "—",          bg: "#F3F4F6", fg: "#6B7280" },
};
const SOURCE_LABEL = { email: "Email", google: "Google", facebook: "Facebook" };

// Hasta dónde llegó quien no terminó la compra (status de students_subscriptions).
const STAGE_PILL = {
  checkout: { label: "Dejó el mail",   bg: "#FEF3C7", fg: "#92400E" },
  created:  { label: "Generó el pago", bg: "#DBEAFE", fg: "#1E40AF" },
  pending:  { label: "Pago pendiente", bg: "#EDE9FE", fg: "#5B21B6" },
};
const YESNO_PILL = {
  yes: { label: "Sí", bg: "#D1FAE5", fg: "#065F46" },
  no:  { label: "No", bg: "#F3F4F6", fg: "#6B7280" },
};

// ────────────────────────────────────────────────────────────────────
//  Estilos
// ────────────────────────────────────────────────────────────────────
const useStyles = makeStyles({
  // Wrapper: rompe el padding del Admin container y pinta el canvas warm.
  canvas: {
    background: T.bg,
    color: T.ink,
    fontFamily: T.fontSans,
    fontSize: 13.5,
    lineHeight: 1.5,
    // El admin envuelve la view en content (padding 0 15 / paddingTop 25) +
    // container (padding 0 15). Comemos los 4 lados para que el canvas slate
    // llegue al borde del mainPanel y no se vea el gris #eeeeee del wrapper.
    margin: "-25px -30px 0",
    padding: "20px 28px 32px",
    minHeight: "calc(100vh - 95px)",
    "@media (max-width: 768px)": {
      margin: "-25px -30px 0",
      padding: "20px 16px 32px",
    },
    "& *::selection": { background: "rgba(99,102,241,0.18)", color: T.ink },
  },

  // ── Header compacto ─────────────────────────────────────
  headerRow: {
    display: "flex", alignItems: "center", gap: 14,
    marginBottom: 22,
  },
  brandIcon: {
    width: 36, height: 36, borderRadius: 8,
    background: `linear-gradient(135deg, ${T.indigo} 0%, ${T.indigoDeep} 100%)`,
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", boxShadow: `0 2px 6px rgba(79,70,229,0.3)`,
    flexShrink: 0,
  },
  titleBlock: { flex: 1, minWidth: 0 },
  eyebrow: {
    fontSize: 11, fontWeight: 600, color: T.ink3,
    textTransform: "none", letterSpacing: 0,
    marginBottom: 2,
  },
  title: {
    fontSize: 17, fontWeight: 700, letterSpacing: "-0.2px",
    color: T.ink, margin: 0, lineHeight: 1.25,
  },

  // ── Selector de audiencia ──────────────────────────────
  audBar: {
    background: T.surface,
    border: `1px solid ${T.line}`,
    borderRadius: 8,
    padding: 6,
    marginBottom: 16,
    display: "flex", gap: 6, flexWrap: "wrap",
  },
  audTab: {
    flex: "1 1 200px",
    textAlign: "left",
    border: `1px solid transparent`,
    background: "transparent",
    borderRadius: 6,
    padding: "9px 13px",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.12s",
    "&:hover": { background: T.hover },
  },
  audTabActive: {
    background: `${T.indigoSoft} !important`,
    borderColor: `${T.indigo} !important`,
  },
  audTabTitle: { fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 2 },
  audTabTitleActive: { color: T.indigoDeep },
  audTabHint: { fontSize: 11.5, color: T.ink3, lineHeight: 1.4 },

  // ── Grid ───────────────────────────────────────────────
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(300px, 340px) 1fr",
    gap: 20,
    alignItems: "start",
    "@media (max-width: 980px)": { gridTemplateColumns: "1fr" },
  },

  // ── Card base ──────────────────────────────────────────
  card: {
    background: T.surface,
    border: `1px solid ${T.line}`,
    borderRadius: 8,
    overflow: "hidden",
  },
  cardHead: {
    padding: "14px 18px 12px",
    borderBottom: `1px solid ${T.line}`,
    display: "flex", alignItems: "center", gap: 12,
  },
  cardHeadTitle: {
    fontSize: 13, fontWeight: 700, color: T.ink,
  },
  cardHeadSpacer: { flex: 1 },
  resetLink: {
    background: "transparent", border: 0, padding: 0,
    fontSize: 12, color: T.ink3, fontWeight: 500,
    cursor: "pointer", textDecoration: "underline",
    textDecorationColor: T.line, textUnderlineOffset: 4,
    transition: "color 0.15s",
    "&:hover": { color: T.indigo, textDecorationColor: T.indigo },
  },
  cardBody: { padding: "16px 18px 18px" },

  // ── Field group ────────────────────────────────────────
  fieldGroup: { marginBottom: 18, "&:last-child": { marginBottom: 0 } },
  fieldLabel: {
    fontSize: 11, fontWeight: 700, color: T.ink3,
    textTransform: "uppercase", letterSpacing: "0.05em",
    marginBottom: 8, display: "flex", alignItems: "center", gap: 8,
  },
  fieldLabelRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 8, gap: 8,
  },
  segmented: {
    display: "inline-flex", border: `1px solid ${T.line}`,
    borderRadius: 6, background: T.bg, padding: 2,
  },
  segBtn: {
    border: 0, background: "transparent",
    fontSize: 11, fontWeight: 600, color: T.ink3,
    padding: "3px 10px", borderRadius: 4, cursor: "pointer",
    fontFamily: "inherit", transition: "all 0.12s",
    "&:hover": { color: T.ink },
  },
  segBtnActive: {
    background: "#fff", color: T.ink,
    boxShadow: "0 1px 2px rgba(15,23,42,0.08)",
  },

  // ── Chips ──────────────────────────────────────────────
  chipsRow: { display: "flex", flexWrap: "wrap", gap: 6 },
  chip: {
    fontSize: 12, fontWeight: 500,
    padding: "5px 11px", borderRadius: 6,
    border: `1px solid ${T.line}`, background: "#fff",
    color: T.ink2, cursor: "pointer", fontFamily: "inherit",
    transition: "all 0.12s",
    "&:hover": { borderColor: "#CBD5E1", background: T.hover },
  },
  chipActive: {
    background: `${T.indigoSoft} !important`,
    color: `${T.indigoDeep} !important`,
    borderColor: `${T.indigo} !important`,
    fontWeight: 600,
  },

  // ── Inputs ─────────────────────────────────────────────
  dateRow: { display: "flex", gap: 10, marginTop: 12 },
  input: {
    flex: 1, padding: "8px 12px",
    border: `1px solid ${T.line}`, borderRadius: 8,
    fontFamily: "inherit", fontSize: 13, outline: "none",
    color: T.ink, background: T.bg,
    transition: "all 0.15s", boxSizing: "border-box", width: "100%",
    "&:focus": { borderColor: T.indigo, background: "#fff", boxShadow: `0 0 0 3px rgba(99,102,241,0.1)` },
    "&::placeholder": { color: T.ink4 },
  },
  select: {
    width: "100%", padding: "8px 12px",
    border: `1px solid ${T.line}`, borderRadius: 8,
    fontFamily: "inherit", fontSize: 13, outline: "none",
    color: T.ink, background: T.bg,
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    backgroundSize: 12,
    paddingRight: 32,
    transition: "all 0.15s",
    "&:focus": { borderColor: T.indigo, background: "#fff", boxShadow: `0 0 0 3px rgba(99,102,241,0.1)` },
    "& option": { background: "#fff", color: T.ink, padding: 6 },
  },
  helper: {
    fontSize: 11.5, color: T.ink4, marginTop: 8, lineHeight: 1.5,
  },
  checkRow: {
    display: "inline-flex", alignItems: "center", gap: 10,
    fontSize: 13, color: T.ink2,
    cursor: "pointer", userSelect: "none",
    "& input": {
      appearance: "none", width: 16, height: 16,
      border: `1px solid ${T.line}`, borderRadius: 4,
      background: "#fff", cursor: "pointer", margin: 0,
      transition: "all 0.15s", position: "relative",
      "&:checked": {
        background: T.indigo, borderColor: T.indigo,
        "&::after": {
          content: '""', position: "absolute",
          left: 5, top: 1.5, width: 4, height: 9,
          border: `solid #fff`, borderWidth: "0 2px 2px 0",
          transform: "rotate(45deg)",
        },
      },
    },
  },

  // ── Resultados ─────────────────────────────────────────
  resultsHead: {
    padding: "20px 22px",
    borderBottom: `1px solid ${T.line}`,
    display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16,
  },
  bigWrap: { flex: 1, minWidth: 180 },
  bigEyebrow: {
    fontSize: 11, fontWeight: 600, color: T.ink3,
    marginBottom: 4,
  },
  bigNumber: {
    fontWeight: 700, fontSize: "clamp(28px, 2.8vw, 36px)",
    letterSpacing: "-0.5px", lineHeight: 1.1,
    color: T.ink,
    fontFeatureSettings: "'tnum' 1",
  },
  bigCaption: {
    fontSize: 12, color: T.ink3, marginTop: 4,
  },
  actions: { display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" },
  btn: {
    fontFamily: "inherit",
    display: "inline-flex", alignItems: "center", gap: 7,
    padding: "8px 14px", border: 0, borderRadius: 8,
    fontSize: 12, fontWeight: 600, cursor: "pointer",
    transition: "all 0.15s",
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  btnPrimary: {
    background: `linear-gradient(135deg, ${T.indigo} 0%, ${T.indigoDeep} 100%)`,
    color: "#fff",
    boxShadow: `0 1px 3px rgba(79,70,229,0.3)`,
    "&:hover:not(:disabled)": { transform: "translateY(-1px)", boxShadow: `0 4px 8px rgba(79,70,229,0.35)` },
  },
  btnGhost: {
    background: "#fff", color: T.ink2,
    border: `1px solid ${T.line}`,
    "&:hover:not(:disabled)": { background: T.hover, borderColor: "#CBD5E1" },
  },

  // ── Tabla ──────────────────────────────────────────────
  tableScroll: { overflowX: "auto" },
  table: {
    width: "100%", borderCollapse: "collapse",
    fontFamily: T.fontSans, fontSize: 13,
  },
  th: {
    textAlign: "left", padding: "10px 18px",
    fontSize: 11, fontWeight: 700, color: T.ink3,
    textTransform: "uppercase", letterSpacing: "0.05em",
    borderBottom: `1px solid ${T.line}`,
    background: T.surface2,
  },
  td: {
    padding: "10px 18px",
    borderBottom: `1px solid ${T.lineSoft}`,
    color: T.ink, verticalAlign: "middle",
    fontSize: 12.5,
  },
  trZebra: {
    "&:hover td": { background: T.hover },
  },
  pill: {
    display: "inline-block",
    fontSize: 10, fontWeight: 700,
    padding: "3px 8px", borderRadius: 4,
    whiteSpace: "nowrap",
  },
  emailCell: { fontWeight: 500, color: T.ink },
  muted: { color: T.ink4 },
  tableFoot: {
    padding: "12px 18px",
    fontSize: 12, color: T.ink3,
    borderTop: `1px solid ${T.lineSoft}`,
  },
  empty: {
    padding: "60px 24px", textAlign: "center", color: T.ink3,
    fontSize: 13,
  },

  // ── Toast / banner ─────────────────────────────────────
  toastWrap: {
    position: "fixed",
    bottom: 24, right: 24, zIndex: 10000,
    display: "flex", flexDirection: "column", gap: 10,
    maxWidth: 380,
  },
  toast: {
    display: "flex", alignItems: "flex-start", gap: 12,
    padding: "12px 16px",
    borderRadius: 10,
    background: "#fff",
    border: `1px solid ${T.line}`,
    boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
    fontSize: 13, color: T.ink,
    animation: "$slideIn 0.2s ease-out",
  },
  toastError: {
    borderColor: "#FCA5A5",
    background: "linear-gradient(135deg, #FEF2F2 0%, #fff 60%)",
  },
  toastSuccess: {
    borderColor: "#86EFAC",
    background: "linear-gradient(135deg, #ECFDF5 0%, #fff 60%)",
  },
  toastIcon: {
    flexShrink: 0,
    width: 28, height: 28, borderRadius: 6,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  toastBody: { flex: 1, minWidth: 0, lineHeight: 1.45 },
  toastTitle: { fontWeight: 600, color: T.ink, marginBottom: 2 },
  toastMsg: { color: T.ink3, fontSize: 12 },
  toastClose: {
    border: 0, background: "transparent", cursor: "pointer",
    color: T.ink4, padding: 4, marginLeft: 4, flexShrink: 0,
    "&:hover": { color: T.ink },
  },

  // ── Spinner ────────────────────────────────────────────
  "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
  "@keyframes slideIn": {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  spinning: { animation: "$spin 0.8s linear infinite", display: "inline-flex", color: T.indigo },
});

// ────────────────────────────────────────────────────────────────────
//  Helpers
// ────────────────────────────────────────────────────────────────────
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  // Formato compacto sin separadores "de" del locale es-AR: 18 mar 2026
  const day = String(d.getDate()).padStart(2, "0");
  const months = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];
  return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
};
const fmtNumber = (n) => (n == null ? "—" : Number(n).toLocaleString("es-AR"));

// Celda de la vista previa según el `type` que declara el backend en `columns`.
const renderCell = (value, type, classes) => {
  const dash = <span className={classes.muted}>—</span>;
  if (type !== "bool" && (value === null || value === undefined || value === "")) {
    return dash;
  }
  if (type === "bool") {
    const p = value ? YESNO_PILL.yes : YESNO_PILL.no;
    return <span className={classes.pill} style={{ background: p.bg, color: p.fg }}>{p.label}</span>;
  }
  if (type === "date" || type === "datetime") return fmtDate(value);
  if (type === "number") return fmtNumber(value);
  if (type === "intent") {
    const n = Number(value);
    if (!n) return dash;
    return `${Math.round(n * 100)}%`;
  }
  if (type === "stage") {
    const p = STAGE_PILL[value];
    if (!p) return String(value);
    return <span className={classes.pill} style={{ background: p.bg, color: p.fg }}>{p.label}</span>;
  }
  if (type === "state") {
    const p = STATE_PILL[value] || STATE_PILL.na;
    return <span className={classes.pill} style={{ background: p.bg, color: p.fg }}>{p.label}</span>;
  }
  if (type === "source") return SOURCE_LABEL[value] || value;
  return String(value);
};

function useDebounced(value, ms = 350) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return d;
}

const INITIAL = {
  audience: "students",
  // — audiencia 'abandoned'
  abandoned_stage: "any",
  include_buyers: false,
  // — audiencia 'inbox'
  mailbox: "all",
  min_intent: "any",
  exclude_automated: true,
  exclude_ignored: true,
  // — comunes a 'abandoned' e 'inbox'
  student_link: "any",
  exclude_test: true,
  // — audiencia 'students'
  search: "",
  activity_preset: "last_1y",
  activity_invert: false,
  activity_from: "",
  activity_to: "",
  created_from: "",
  created_to: "",
  states: [],
  sources: [],
  countries: [],
  subscription_status: "any",
  plans: [],
  expired_recency: "any",
  expiring_in: "any",
  expiring_min_days: "",
  expiring_max_days: "",
  only_confirmed: false,
};

// ────────────────────────────────────────────────────────────────────
//  Componente
// ────────────────────────────────────────────────────────────────────
export default function MailingExport() {
  const classes = useStyles();

  const [cfg, setCfg] = useState({
    countries: [], states: [], sources: [],
    subscriptionStatus: [], activityPresets: [],
    plans: [], expiredRecency: [], expiringWindow: [],
    audiences: [], abandonedStages: [], mailboxes: [], inboxIntent: [], studentLink: [],
    loaded: false,
  });
  const [filters, setFilters] = useState(INITIAL);
  const [data, setData] = useState({ count: null, sample: [], columns: [] });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [toasts, setToasts] = useState([]);

  const pushToast = useCallback((kind, title, msg) => {
    const id = Date.now() + Math.random();
    setToasts((ts) => [...ts, { id, kind, title, msg }]);
    // Auto-dismiss a los 6s.
    setTimeout(() => setToasts((ts) => ts.filter((t) => t.id !== id)), 6000);
  }, []);
  const dismissToast = (id) => setToasts((ts) => ts.filter((t) => t.id !== id));

  const debouncedFilters = useDebounced(filters, 350);

  useEffect(() => {
    fetch(`${API_URI}/admin/mailing-export/cfg`)
      .then((r) => r.json())
      .then((j) => setCfg({ ...j, loaded: true }))
      .catch(() => setCfg((c) => ({ ...c, loaded: true })));
  }, []);

  const buildBody = useCallback((f) => {
    const fd = new FormData();
    const aud = f.audience || "students";
    fd.append("audience", aud);

    // Comunes a las tres audiencias.
    if (f.search && f.search.trim()) fd.append("search", f.search.trim());
    if (f.created_from) fd.append("created_from", f.created_from);
    if (f.created_to) fd.append("created_to", f.created_to);

    if (aud !== "students") {
      (f.countries || []).forEach((s) => fd.append("countries[]", s));
      if (f.student_link && f.student_link !== "any") fd.append("student_link", f.student_link);
      if (!f.exclude_test) fd.append("exclude_test", 0);
    }

    if (aud === "abandoned") {
      if (f.abandoned_stage && f.abandoned_stage !== "any") fd.append("abandoned_stage", f.abandoned_stage);
      if (f.include_buyers) fd.append("include_buyers", 1);
      (f.plans || []).forEach((s) => fd.append("plans[]", s));
      return fd;
    }

    if (aud === "inbox") {
      if (f.mailbox && f.mailbox !== "all") fd.append("mailbox", f.mailbox);
      if (f.min_intent && f.min_intent !== "any") fd.append("min_intent", f.min_intent);
      if (!f.exclude_automated) fd.append("exclude_automated", 0);
      if (!f.exclude_ignored) fd.append("exclude_ignored", 0);
      return fd;
    }

    if (f.activity_preset) fd.append("activity_preset", f.activity_preset);
    if (f.activity_invert) fd.append("activity_invert", 1);
    if (f.activity_preset === "custom") {
      if (f.activity_from) fd.append("activity_from", f.activity_from);
      if (f.activity_to) fd.append("activity_to", f.activity_to);
    }
    (f.states || []).forEach((s) => fd.append("states[]", s));
    (f.sources || []).forEach((s) => fd.append("sources[]", s));
    (f.countries || []).forEach((s) => fd.append("countries[]", s));
    (f.plans || []).forEach((s) => fd.append("plans[]", s));
    if (f.subscription_status && f.subscription_status !== "any")
      fd.append("subscription_status", f.subscription_status);
    if (f.expired_recency && f.expired_recency !== "any")
      fd.append("expired_recency", f.expired_recency);
    if (f.expiring_in && f.expiring_in !== "any") {
      fd.append("expiring_in", f.expiring_in);
      if (f.expiring_in === "custom") {
        if (f.expiring_min_days !== "") fd.append("expiring_min_days", f.expiring_min_days);
        if (f.expiring_max_days !== "") fd.append("expiring_max_days", f.expiring_max_days);
      }
    }
    if (f.only_confirmed) fd.append("only_confirmed", 1);
    return fd;
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URI}/admin/mailing-export/preview`, {
      method: "POST",
      body: buildBody(debouncedFilters),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => setData({ count: j.count ?? 0, sample: j.sample ?? [], columns: j.columns ?? [] }))
      .catch((e) => {
        setData({ count: 0, sample: [], columns: [] });
        pushToast("error", "No se pudo calcular el segmento", e.message || "Reintentá en un momento.");
      })
      .finally(() => setLoading(false));
  }, [debouncedFilters, buildBody, pushToast]);

  const toggleInArray = (key, value) => {
    setFilters((f) => {
      const arr = f[key] || [];
      return {
        ...f,
        [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value],
      };
    });
  };
  const setField = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  // "Limpiar" no cambia de audiencia: sólo devuelve los filtros de la actual a su default.
  const resetFilters = () => setFilters((f) => ({ ...INITIAL, audience: f.audience }));

  const audience = filters.audience || "students";
  const AUD_COPY = {
    students:  { eyebrow: "Alumnos en el segmento",   noun: "alumnos",  file: "alumnos" },
    abandoned: { eyebrow: "Compras sin terminar",     noun: "personas", file: "compras_sin_terminar" },
    inbox:     { eyebrow: "Nos escribieron por mail", noun: "personas", file: "nos_escribieron" },
    leads:     { eyebrow: "Dejaron el correo",        noun: "personas", file: "dejaron_el_correo" },
  };
  const copy = AUD_COPY[audience] || AUD_COPY.students;
  // Columnas visibles de la tabla (el backend marca table:false para las que sólo van al CSV).
  const tableCols = (data.columns || []).filter((c) => c.table !== false);

  const downloadCsv = () => {
    setDownloading(true);
    fetch(`${API_URI}/admin/mailing-export/csv`, { method: "POST", body: buildBody(filters) })
      .then(async (r) => {
        if (!r.ok) {
          const txt = await r.text().catch(() => "");
          throw new Error(`HTTP ${r.status}${txt ? ` · ${txt.slice(0, 120)}` : ""}`);
        }
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mailing_${copy.file}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        pushToast("success", "Lista descargada",
          `${fmtNumber(data.count)} ${copy.noun} exportados como CSV.`);
      })
      .catch((e) =>
        pushToast("error", "No se pudo descargar la lista",
          e.message || "Reintentá en un momento."))
      .finally(() => setDownloading(false));
  };

  return (
    <div className={classes.canvas}>

      {/* ── Qué lista se está armando ── */}
      <div className={classes.audBar}>
        {(cfg.audiences || []).map((a) => {
          const on = audience === a.value;
          return (
            <button key={a.value}
              className={`${classes.audTab} ${on ? classes.audTabActive : ""}`}
              onClick={() => setFilters({ ...INITIAL, audience: a.value })}>
              <div className={`${classes.audTabTitle} ${on ? classes.audTabTitleActive : ""}`}>
                {a.display}
              </div>
              <div className={classes.audTabHint}>{a.hint}</div>
            </button>
          );
        })}
      </div>

      <div className={classes.grid}>
        {/* ── Filtros ── */}
        <aside className={classes.card}>
          <div className={classes.cardHead}>
            <span className={classes.cardHeadTitle}>Filtros</span>
            <div className={classes.cardHeadSpacer} />
            <button className={classes.resetLink} onClick={resetFilters}>Limpiar</button>
          </div>
          <div className={classes.cardBody}>

            {/* Buscador */}
            <div className={classes.fieldGroup}>
              <div className={classes.fieldLabel}>Buscar</div>
              <input type="text" className={classes.input}
                placeholder={audience === "students" ? "Email, nombre o apellido"
                  : audience === "inbox" ? "Email o nombre del remitente"
                  : audience === "leads" ? "Email o nombre" : "Email"}
                value={filters.search}
                onChange={(e) => setField("search", e.target.value)} />
            </div>

            {/* ═══ Compras sin terminar ═══ */}
            {audience === "abandoned" && (
              <>
                <div className={classes.fieldGroup}>
                  <div className={classes.fieldLabel}>Hasta dónde llegó</div>
                  <select className={classes.select}
                    value={filters.abandoned_stage}
                    onChange={(e) => setField("abandoned_stage", e.target.value)}>
                    {(cfg.abandonedStages || []).map((s) => (
                      <option key={s.value} value={s.value}>{s.display}</option>
                    ))}
                  </select>
                  <div className={classes.helper}>
                    El checkout reusa la misma fila a medida que la persona avanza: la que quedó
                    frenada en una de estas etapas es una compra que no se concretó.
                  </div>
                </div>

                <div className={classes.fieldGroup}>
                  <div className={classes.fieldLabel}>
                    <Icon name="calendar" size={11} /> Fecha del intento
                  </div>
                  <div className={classes.dateRow} style={{ marginTop: 0 }}>
                    <input type="date" className={classes.input}
                      value={filters.created_from}
                      onChange={(e) => setField("created_from", e.target.value)} />
                    <input type="date" className={classes.input}
                      value={filters.created_to}
                      onChange={(e) => setField("created_to", e.target.value)} />
                  </div>
                </div>

                <div className={classes.fieldGroup}>
                  <div className={classes.fieldLabel}>Plan que intentó comprar</div>
                  <div className={classes.chipsRow}>
                    {(cfg.plans || []).map((p) => (
                      <button key={p.value}
                        className={`${classes.chip} ${filters.plans.includes(p.value) ? classes.chipActive : ""}`}
                        onClick={() => toggleInArray("plans", p.value)}>
                        {p.display}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={classes.fieldGroup}>
                  <label className={classes.checkRow}>
                    <input type="checkbox"
                      checked={filters.include_buyers}
                      onChange={(e) => setField("include_buyers", e.target.checked)} />
                    Incluir a los que compraron antes
                  </label>
                  <div className={classes.helper}>
                    Por defecto sólo aparece quien NUNCA compró. Marcalo para sumar a los que ya
                    habían comprado y abandonaron un intento posterior (renovaciones caídas).
                  </div>
                </div>
              </>
            )}

            {/* ═══ Nos escribieron por mail ═══ */}
            {audience === "inbox" && (
              <>
                <div className={classes.fieldGroup}>
                  <div className={classes.fieldLabel}>
                    <Icon name="mail" size={11} /> Buzón
                  </div>
                  <select className={classes.select}
                    value={filters.mailbox}
                    onChange={(e) => setField("mailbox", e.target.value)}>
                    {(cfg.mailboxes || []).map((m) => (
                      <option key={m.value} value={m.value}>{m.display}</option>
                    ))}
                  </select>
                </div>

                <div className={classes.fieldGroup}>
                  <div className={classes.fieldLabel}>
                    <Icon name="calendar" size={11} /> Fecha del mensaje
                  </div>
                  <div className={classes.dateRow} style={{ marginTop: 0 }}>
                    <input type="date" className={classes.input}
                      value={filters.created_from}
                      onChange={(e) => setField("created_from", e.target.value)} />
                    <input type="date" className={classes.input}
                      value={filters.created_to}
                      onChange={(e) => setField("created_to", e.target.value)} />
                  </div>
                </div>

                <div className={classes.fieldGroup}>
                  <div className={classes.fieldLabel}>
                    <Icon name="spark" size={11} /> Intención de compra
                  </div>
                  <select className={classes.select}
                    value={filters.min_intent}
                    onChange={(e) => setField("min_intent", e.target.value)}>
                    {(cfg.inboxIntent || []).map((s) => (
                      <option key={s.value} value={s.value}>{s.display}</option>
                    ))}
                  </select>
                  <div className={classes.helper}>
                    La puntuación que le puso la IA al mensaje en el inbox.
                  </div>
                </div>

                <div className={classes.fieldGroup}>
                  <label className={classes.checkRow}>
                    <input type="checkbox"
                      checked={filters.exclude_automated}
                      onChange={(e) => setField("exclude_automated", e.target.checked)} />
                    Excluir notificaciones y no-reply
                  </label>
                  <div className={classes.helper}>
                    Saca avisos de Mercado Pago, PayPal, Google y demás remitentes automáticos.
                  </div>
                </div>

                <div className={classes.fieldGroup}>
                  <label className={classes.checkRow}>
                    <input type="checkbox"
                      checked={filters.exclude_ignored}
                      onChange={(e) => setField("exclude_ignored", e.target.checked)} />
                    Excluir lo marcado como spam o ignorado
                  </label>
                </div>
              </>
            )}

            {/* ═══ Dejaron el correo ═══ */}
            {audience === "leads" && (
              <>
                <div className={classes.fieldGroup}>
                  <div className={classes.fieldLabel}>
                    <Icon name="calendar" size={11} /> Cuándo dejó el correo
                  </div>
                  <div className={classes.dateRow} style={{ marginTop: 0 }}>
                    <input type="date" className={classes.input}
                      value={filters.created_from}
                      onChange={(e) => setField("created_from", e.target.value)} />
                    <input type="date" className={classes.input}
                      value={filters.created_to}
                      onChange={(e) => setField("created_to", e.target.value)} />
                  </div>
                  <div className={classes.helper}>
                    La fecha en que entró a la lista, no la del último contacto.
                  </div>
                </div>

                <div className={classes.fieldGroup}>
                  <label className={classes.checkRow}>
                    <input type="checkbox"
                      checked={filters.include_buyers}
                      onChange={(e) => setField("include_buyers", e.target.checked)} />
                    Incluir a los que ya compraron
                  </label>
                  <div className={classes.helper}>
                    Por defecto no aparecen: ofrecerle el imán de captación a alguien que ya
                    pagó es la forma más rápida de quemar la campaña.
                  </div>
                </div>
              </>
            )}

            {/* ═══ Comunes a las audiencias que no son "alumnos" ═══ */}
            {audience !== "students" && (
              <>
                <div className={classes.fieldGroup}>
                  <div className={classes.fieldLabel}>Cuenta en el sitio</div>
                  <select className={classes.select}
                    value={filters.student_link}
                    onChange={(e) => setField("student_link", e.target.value)}>
                    {(cfg.studentLink || []).map((s) => (
                      <option key={s.value} value={s.value}>{s.display}</option>
                    ))}
                  </select>
                </div>

                <div className={classes.fieldGroup}>
                  <div className={classes.fieldLabel}>País</div>
                  <select className={classes.select}
                    value={filters.countries[0] || ""}
                    onChange={(e) => setField("countries", e.target.value ? [e.target.value] : [])}
                    disabled={audience === "inbox" || audience === "leads"}>
                    <option value="">Todos</option>
                    {cfg.countries.map((c) => (
                      <option key={c.value} value={c.value}>{c.display}</option>
                    ))}
                  </select>
                  {audience === "inbox" && (
                    <div className={classes.helper}>Un correo entrante no trae país.</div>
                  )}
                  {audience === "leads" && (
                    <div className={classes.helper}>
                      La landing sólo pide nombre y correo: no hay país.
                    </div>
                  )}
                </div>

                <div className={classes.fieldGroup}>
                  <label className={classes.checkRow}>
                    <input type="checkbox"
                      checked={filters.exclude_test}
                      onChange={(e) => setField("exclude_test", e.target.checked)} />
                    Excluir mails de prueba
                  </label>
                </div>
              </>
            )}

            {/* ═══ Alumnos registrados ═══ */}
            {audience === "students" && (
            <>
            {/* Última actividad */}
            <div className={classes.fieldGroup}>
              <div className={classes.fieldLabelRow}>
                <div className={classes.fieldLabel} style={{ marginBottom: 0 }}>
                  <Icon name="calendar" size={11} /> Última actividad
                </div>
                <div className={classes.segmented}>
                  <button
                    className={`${classes.segBtn} ${!filters.activity_invert ? classes.segBtnActive : ""}`}
                    onClick={() => setField("activity_invert", false)}
                    title="Sí entraron"
                  >
                    Con
                  </button>
                  <button
                    className={`${classes.segBtn} ${filters.activity_invert ? classes.segBtnActive : ""}`}
                    onClick={() => setField("activity_invert", true)}
                    title="No entraron"
                  >
                    Sin
                  </button>
                </div>
              </div>
              <div className={classes.chipsRow}>
                {(cfg.activityPresets || []).map((p) => (
                  <button
                    key={p.value}
                    className={`${classes.chip} ${filters.activity_preset === p.value ? classes.chipActive : ""}`}
                    onClick={() => setField("activity_preset", p.value)}
                  >
                    {p.display}
                  </button>
                ))}
              </div>
              {filters.activity_preset === "custom" && (
                <div className={classes.dateRow}>
                  <input type="date" className={classes.input}
                    value={filters.activity_from}
                    onChange={(e) => setField("activity_from", e.target.value)} />
                  <input type="date" className={classes.input}
                    value={filters.activity_to}
                    onChange={(e) => setField("activity_to", e.target.value)} />
                </div>
              )}
            </div>

            {/* Fecha de registro */}
            <div className={classes.fieldGroup}>
              <div className={classes.fieldLabel}>
                <Icon name="calendar" size={11} /> Fecha de registro
              </div>
              <div className={classes.dateRow} style={{ marginTop: 0 }}>
                <input type="date" className={classes.input}
                  value={filters.created_from}
                  onChange={(e) => setField("created_from", e.target.value)} />
                <input type="date" className={classes.input}
                  value={filters.created_to}
                  onChange={(e) => setField("created_to", e.target.value)} />
              </div>
            </div>

            {/* Estado */}
            <div className={classes.fieldGroup}>
              <div className={classes.fieldLabel}>Estado del alumno</div>
              <div className={classes.chipsRow}>
                {cfg.states.map((s) => (
                  <button key={s.value}
                    className={`${classes.chip} ${filters.states.includes(s.value) ? classes.chipActive : ""}`}
                    onClick={() => toggleInArray("states", s.value)}>
                    {s.display}
                  </button>
                ))}
              </div>
            </div>

            {/* Origen */}
            <div className={classes.fieldGroup}>
              <div className={classes.fieldLabel}>Origen de registro</div>
              <div className={classes.chipsRow}>
                {cfg.sources.map((s) => (
                  <button key={s.value}
                    className={`${classes.chip} ${filters.sources.includes(s.value) ? classes.chipActive : ""}`}
                    onClick={() => toggleInArray("sources", s.value)}>
                    {s.display}
                  </button>
                ))}
              </div>
            </div>

            {/* Suscripción */}
            <div className={classes.fieldGroup}>
              <div className={classes.fieldLabel}>Suscripción</div>
              <select className={classes.select}
                value={filters.subscription_status}
                onChange={(e) => setField("subscription_status", e.target.value)}>
                {cfg.subscriptionStatus.map((s) => (
                  <option key={s.value} value={s.value}>{s.display}</option>
                ))}
              </select>
            </div>

            {/* Antigüedad del vencimiento */}
            <div className={classes.fieldGroup}>
              <div className={classes.fieldLabel}>Antigüedad del vencimiento</div>
              <select className={classes.select}
                value={filters.expired_recency}
                onChange={(e) => setField("expired_recency", e.target.value)}>
                {(cfg.expiredRecency || []).map((s) => (
                  <option key={s.value} value={s.value}>{s.display}</option>
                ))}
              </select>
            </div>

            {/* Vencimiento próximo (a quién se le vence el plan dentro de N días) */}
            <div className={classes.fieldGroup}>
              <div className={classes.fieldLabel}>
                <Icon name="calendar" size={11} /> Vence dentro de
              </div>
              <div className={classes.chipsRow}>
                {(cfg.expiringWindow || []).map((w) => (
                  <button key={w.value}
                    className={`${classes.chip} ${filters.expiring_in === w.value ? classes.chipActive : ""}`}
                    onClick={() => setField("expiring_in", filters.expiring_in === w.value ? "any" : w.value)}>
                    {w.display}
                  </button>
                ))}
              </div>
              {filters.expiring_in === "custom" && (
                <>
                  <div className={classes.dateRow}>
                    <input type="number" min="0" className={classes.input}
                      placeholder="Desde (días)"
                      value={filters.expiring_min_days}
                      onChange={(e) => setField("expiring_min_days", e.target.value)} />
                    <input type="number" min="0" className={classes.input}
                      placeholder="Hasta (días)"
                      value={filters.expiring_max_days}
                      onChange={(e) => setField("expiring_max_days", e.target.value)} />
                  </div>
                  <div className={classes.helper}>
                    Ej.: entre 30 y 45 días para la próxima ola de renovaciones. Dejá "Desde" vacío para incluir desde hoy.
                  </div>
                </>
              )}
              {filters.expiring_in !== "any" && filters.expiring_in !== "custom" && (
                <div className={classes.helper}>
                  Alumnos con acceso vigente cuyo plan vence en ese lapso.
                </div>
              )}
            </div>

            {/* Plan suscripto */}
            <div className={classes.fieldGroup}>
              <div className={classes.fieldLabel}>Plan suscripto</div>
              <div className={classes.chipsRow}>
                {(cfg.plans || []).map((p) => (
                  <button key={p.value}
                    className={`${classes.chip} ${filters.plans.includes(p.value) ? classes.chipActive : ""}`}
                    onClick={() => toggleInArray("plans", p.value)}>
                    {p.display}
                  </button>
                ))}
              </div>
            </div>

            {/* País */}
            <div className={classes.fieldGroup}>
              <div className={classes.fieldLabel}>País</div>
              <select className={classes.select}
                value={filters.countries[0] || ""}
                onChange={(e) => setField("countries", e.target.value ? [e.target.value] : [])}>
                <option value="">Todos</option>
                {cfg.countries.map((c) => (
                  <option key={c.value} value={c.value}>{c.display}</option>
                ))}
              </select>
            </div>

            {/* Confirmados */}
            <div className={classes.fieldGroup}>
              <label className={classes.checkRow}>
                <input type="checkbox"
                  checked={filters.only_confirmed}
                  onChange={(e) => setField("only_confirmed", e.target.checked)} />
                Sólo emails confirmados
              </label>
            </div>
            </>
            )}
          </div>
        </aside>

        {/* ── Resultados ── */}
        <section>
          <div className={classes.card}>
            <div className={classes.resultsHead}>
              <div className={classes.bigWrap}>
                <div className={classes.bigEyebrow}>{copy.eyebrow}</div>
                <div className={classes.bigNumber}>
                  {loading ? (
                    <span className={classes.spinning}><Icon name="refresh" size={42} /></span>
                  ) : fmtNumber(data.count)}
                </div>
                <div className={classes.bigCaption}>
                  Actualizado en tiempo real al cambiar los filtros.
                </div>
              </div>
              <div className={classes.actions}>
                <button className={`${classes.btn} ${classes.btnGhost}`}
                  onClick={() => setFilters({ ...filters })}
                  disabled={loading}>
                  <Icon name="refresh" size={14} /> Refrescar
                </button>
                <button className={`${classes.btn} ${classes.btnPrimary}`}
                  onClick={downloadCsv}
                  disabled={downloading || loading || !data.count}>
                  <Icon name="download" size={14} />
                  {downloading ? "Generando…" : "Descargar CSV"}
                </button>
              </div>
            </div>

            <div className={classes.tableScroll}>
              {data.sample.length === 0 ? (
                <div className={classes.empty}>
                  {loading ? "Calculando…" : "Sin coincidencias con estos filtros."}
                </div>
              ) : audience !== "students" ? (
                <table className={classes.table}>
                  <thead>
                    <tr>
                      {tableCols.map((c) => (
                        <th key={c.key} className={classes.th}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.sample.map((r) => (
                      <tr key={r.mail} className={classes.trZebra}>
                        {tableCols.map((c) => (
                          <td key={c.key}
                            className={`${classes.td} ${c.type === "email" ? classes.emailCell : ""}`}>
                            {renderCell(r[c.key], c.type, classes)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th className={classes.th}>Email</th>
                      <th className={classes.th}>Nombre</th>
                      <th className={classes.th}>Estado</th>
                      <th className={classes.th}>Vence plan</th>
                      <th className={classes.th}>Origen</th>
                      <th className={classes.th}>Último login</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sample.map((s) => {
                      const pill = STATE_PILL[s.state] || STATE_PILL.na;
                      const full = [s.name, s.lastname].filter(Boolean).join(" ").trim();
                      return (
                        <tr key={s.id} className={classes.trZebra}>
                          <td className={`${classes.td} ${classes.emailCell}`}>{s.mail}</td>
                          <td className={classes.td}>
                            {full ? full : <span className={classes.muted}>—</span>}
                          </td>
                          <td className={classes.td}>
                            <span className={classes.pill}
                              style={{ background: pill.bg, color: pill.fg }}>
                              {pill.label}
                            </span>
                          </td>
                          <td className={classes.td}>
                            {s.plan_expiry ? fmtDate(s.plan_expiry) : <span className={classes.muted}>—</span>}
                          </td>
                          <td className={classes.td}>
                            {SOURCE_LABEL[s.source_login] || s.source_login || <span className={classes.muted}>—</span>}
                          </td>
                          <td className={classes.td}>
                            {s.last_login ? fmtDate(s.last_login) : <span className={classes.muted}>nunca</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {data.count > data.sample.length && (
                <div className={classes.tableFoot}>
                  Mostrando {data.sample.length} de {fmtNumber(data.count)} resultados. El CSV incluye el total.
                </div>
              )}
            </div>
          </div>

        </section>
      </div>

      {/* Toasts in-page (sin alert nativo) */}
      {toasts.length > 0 && (
        <div className={classes.toastWrap}>
          {toasts.map((t) => {
            const meta = t.kind === "error"
              ? { cls: classes.toastError, icon: "alert", bg: "#FEE2E2", fg: "#B91C1C" }
              : { cls: classes.toastSuccess, icon: "check", bg: "#D1FAE5", fg: "#047857" };
            return (
              <div key={t.id} className={`${classes.toast} ${meta.cls}`}>
                <div className={classes.toastIcon} style={{ background: meta.bg, color: meta.fg }}>
                  <Icon name={meta.icon} size={16} strokeWidth={2.2} />
                </div>
                <div className={classes.toastBody}>
                  <div className={classes.toastTitle}>{t.title}</div>
                  {t.msg && <div className={classes.toastMsg}>{t.msg}</div>}
                </div>
                <button
                  className={classes.toastClose}
                  onClick={() => dismissToast(t.id)}
                  aria-label="Cerrar"
                >
                  <Icon name="x" size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
